import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Chip, ListItemText, Typography } from "@mui/material";
import {
  type DocumentUIStatus,
  documentValidityLabelFr,
  evaluateDocumentValidity,
  getDocumentStatus,
} from "@repo/types";
import { formatDate } from "@repo/utils";
import { t } from "i18next";

type Props = {
  documentTitle: string;
  /** Appended to the title, e.g. " - AG" for system-generated documents. */
  titleSuffix?: string;
  isPDF: boolean;
  isImage: boolean;
  updatedAt: string;
  error?: { message?: string };
  versionStatus?: string;
  documentState?: string | null;
  rejectionReason?: string;
  additionalInfo?: string;
  handledBy?: string | null;
  expiresAt?: string | null;
  documentName?: string;
  statusOverride?: DocumentUIStatus;
  /** Off where a review panel already shows the document's validity. */
  showValidity?: boolean;
  /** Off outside the driver review surface. */
  showStatus?: boolean;
};

const STATUS_CONFIG: Record<
  DocumentUIStatus,
  {
    label: string;
    color: "success" | "error" | "warning" | "info" | "default";
    icon: React.JSX.Element;
  }
> = {
  APPROVED: {
    label: "Approuvé",
    color: "success",
    icon: <CheckCircleIcon sx={{ fontSize: 12 }} />,
  },
  PENDING_REVIEW: {
    label: "En attente",
    color: "warning",
    icon: <HourglassEmptyIcon sx={{ fontSize: 12 }} />,
  },
  PROCESSING: {
    label: "En traitement",
    color: "info",
    icon: <HourglassEmptyIcon sx={{ fontSize: 12 }} />,
  },
  REJECTED: { label: "Rejeté", color: "error", icon: <CancelIcon sx={{ fontSize: 12 }} /> },
  REMOVED: { label: "Supprimé", color: "default", icon: <CancelIcon sx={{ fontSize: 12 }} /> },
  SUPERSEDED: {
    label: "Remplacé",
    color: "default",
    icon: <HelpOutlineIcon sx={{ fontSize: 12 }} />,
  },
  MISSING: { label: "Manquant", color: "default", icon: <HelpOutlineIcon sx={{ fontSize: 12 }} /> },
  INCOMPLETE_DATA: {
    label: "Incomplet",
    color: "error",
    icon: <WarningIcon sx={{ fontSize: 12 }} />,
  },
};

const EXPIRED_CONFIG = {
  label: "Expiré",
  color: "error" as const,
  icon: <WarningIcon sx={{ fontSize: 12 }} />,
};

export function DocumentListItemText({
  documentTitle,
  titleSuffix = "",
  isPDF,
  isImage,
  updatedAt,
  error,
  versionStatus,
  documentState: _documentState,
  rejectionReason,
  additionalInfo,
  handledBy: _handledBy,
  expiresAt,
  documentName,
  statusOverride,
  showValidity = true,
  showStatus = true,
}: Props) {
  const derived = !showStatus
    ? null
    : statusOverride
      ? { status: statusOverride }
      : getDocumentStatus({
          dbDoc: versionStatus
            ? {
                version_status: versionStatus,
                key: "present",
                bucket: "present",
                rejectionReason,
                additionalInfo,
              }
            : null,
        });

  const isApproved = derived?.status === "APPROVED";
  const validity = evaluateDocumentValidity(documentName ?? "", expiresAt, new Date());
  const isExpired = isApproved && validity.blocksWork;
  const stateCfg = isExpired
    ? EXPIRED_CONFIG
    : derived
      ? (STATUS_CONFIG[derived.status] ?? STATUS_CONFIG.MISSING)
      : null;
  const formattedExpiresAt = expiresAt ? formatDate(expiresAt) : null;

  return (
    <ListItemText
      primary={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "70%",
              textTransform: "capitalize",
            }}
          >
            {documentTitle}
            {titleSuffix}
          </Typography>
          {stateCfg && (
            <Chip
              icon={stateCfg.icon}
              label={stateCfg.label}
              color={stateCfg.color}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
            />
          )}
        </Box>
      }
      secondary={
        <>
          <Typography color="text.secondary" component="span" variant="caption">
            {t("common.actions.update-label")} {updatedAt}
          </Typography>
          {showValidity && isApproved && documentName && (
            <Typography
              component="span"
              variant="caption"
              color={
                validity.state === "VALID" || validity.state === "NON_EXPIRING"
                  ? "text.secondary"
                  : "warning.main"
              }
              display="block"
            >
              {documentValidityLabelFr(documentName, expiresAt, new Date())}
            </Typography>
          )}
          {showValidity && isApproved && formattedExpiresAt && (
            <>
              <br />
              <Typography color="text.secondary" component="span" variant="caption">
                {isExpired ? "Expiré le" : t("document.expiresAt", { defaultValue: "Expire le" })}{" "}
                {formattedExpiresAt}
              </Typography>
            </>
          )}
          <br />
          <Typography color="text.secondary" component="span" variant="caption">
            Type: {isPDF ? "PDF" : isImage ? "Image" : "Unknown"}
          </Typography>

          {error?.message !== undefined && error.message.length > 0 && (
            <>
              <br />
              <Typography color="error" component="span" variant="caption">
                {error.message}
              </Typography>
            </>
          )}
        </>
      }
    />
  );
}
